import { useState, useEffect } from 'react';
import { api } from '../services/api';
import { StripeConnectStatus, StripeMode, StripeModeStatus } from '../types';
import { ExternalLink, ChevronRight, CheckCircle2, ShieldAlert, FlaskConical, Globe2, Lock } from 'lucide-react';

export function AdminStripe() {
  const [status, setStatus] = useState<StripeConnectStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyMode, setBusyMode] = useState<StripeMode | null>(null);

  useEffect(() => { loadStatus(); }, []);

  const loadStatus = async () => {
    try { setStatus(await api.getStripeConnectStatus()); }
    catch (err: any) { setError(err.message); }
    finally { setLoading(false); }
  };

  const handleConnect = async (mode: StripeMode) => {
    setError('');
    setBusyMode(mode);
    try {
      const { url } = await api.initiateStripeOnboarding(mode);
      window.location.href = url;
    } catch (err: any) {
      setError(err.message);
      setBusyMode(null);
    }
  };

  const handleDashboard = async (mode: StripeMode) => {
    setError('');
    try {
      const { url } = await api.getStripeDashboardLink(mode);
      window.open(url, '_blank');
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleDisconnect = async (mode: StripeMode) => {
    const confirmed = window.confirm(
      `Disconnect your ${mode === 'live' ? 'LIVE' : 'TEST'} Stripe account? You will need to reconnect to accept ${mode} payments.`
    );
    if (!confirmed) return;
    setError('');
    setBusyMode(mode);
    try {
      await api.disconnectStripeAccount(mode);
      await loadStatus();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setBusyMode(null);
    }
  };

  const handleSetMode = async (mode: StripeMode) => {
    if (!status || status.activeMode === mode) return;
    setError('');
    setBusyMode(mode);
    try {
      await api.setStripeMode(mode);
      await loadStatus();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setBusyMode(null);
    }
  };

  if (loading) return <div className="flex items-center justify-center h-96 text-slate-400 text-lg">Loading...</div>;

  const platformManaged = status ? !status.multiTenant : false;

  return (
    <div className="max-w-4xl mx-auto pb-24">
      {/* Header */}
      <div className="mb-6 sticky top-0 bg-[#f0f4f8]/80 backdrop-blur-md py-4 z-10 border-b border-transparent">
        <h1 className="text-3xl font-extrabold text-slate-900 tracking-tight">Stripe Integration</h1>
        <p className="text-slate-500 font-medium mt-1">Connect your Stripe accounts and toggle between live and test mode.</p>
      </div>

      {error && (
        <div className="px-5 py-4 bg-red-50 text-red-600 border border-red-100 rounded-2xl mb-6 font-medium">
          {error}
        </div>
      )}

      {platformManaged && (
        <div className="px-5 py-4 bg-slate-100 text-slate-700 border border-slate-200 rounded-2xl mb-6 font-medium flex items-start gap-3">
          <Lock className="w-5 h-5 mt-0.5 shrink-0 text-slate-500" />
          <div>
            <div className="font-bold text-slate-900">Stripe is managed by the platform</div>
            <div className="text-[14px] text-slate-600 mt-0.5">All transactions on this workspace are processed through the platform's Stripe account. Per-workspace Stripe connection is disabled.</div>
          </div>
        </div>
      )}

      {/* Active mode toggle */}
      {status && (
        <div className="bg-white p-5 rounded-[2rem] shadow-sm border border-slate-200/60 mb-6">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <h2 className="text-[13px] font-bold text-slate-400 uppercase tracking-widest">Active Mode</h2>
              <p className="text-[15px] font-medium text-slate-600 mt-1">
                {platformManaged
                  ? <>Transactions run through the platform's <span className="font-bold text-slate-800">{status.activeMode === 'live' ? 'Live' : 'Test'}</span> Stripe account.</>
                  : <>Transactions on this workspace will run through your <span className="font-bold text-slate-800">{status.activeMode === 'live' ? 'Live' : 'Test'}</span> Stripe account.</>
                }
              </p>
            </div>
            <div className="flex items-center bg-slate-100 rounded-2xl p-1">
              <ModeToggleButton
                active={status.activeMode === 'test'}
                mode="test"
                disabled={busyMode !== null}
                onClick={() => handleSetMode('test')}
              />
              <ModeToggleButton
                active={status.activeMode === 'live'}
                mode="live"
                disabled={busyMode !== null}
                onClick={() => handleSetMode('live')}
              />
            </div>
          </div>
        </div>
      )}

      {status && (
        platformManaged
          ? <SingleTenantPanel activeMode={status.activeMode} />
          : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              <StripeConnectionCard
                mode="test"
                active={status.activeMode === 'test'}
                status={status.test}
                busy={busyMode === 'test'}
                onConnect={() => handleConnect('test')}
                onDashboard={() => handleDashboard('test')}
                onDisconnect={() => handleDisconnect('test')}
              />
              <StripeConnectionCard
                mode="live"
                active={status.activeMode === 'live'}
                status={status.live}
                busy={busyMode === 'live'}
                onConnect={() => handleConnect('live')}
                onDashboard={() => handleDashboard('live')}
                onDisconnect={() => handleDisconnect('live')}
              />
            </div>
          )
      )}

      <div className="bg-white p-8 rounded-[2.5rem] shadow-sm border border-slate-200/60 transition-all">
        <h3 className="text-xl font-extrabold text-slate-800 mb-4">About Stripe Modes</h3>
        {platformManaged ? (
          <p className="text-[15px] font-medium text-slate-500 leading-relaxed mb-3">
            The platform has two sets of Stripe keys — <strong className="text-slate-800">test</strong> (sandbox, no real money) and <strong className="text-slate-800">live</strong> (real transactions). Use the toggle above to switch which set is active for this workspace.
          </p>
        ) : (
          <p className="text-[15px] font-medium text-slate-500 leading-relaxed mb-3">
            You can connect both a <strong className="text-slate-800">test</strong> Stripe account (for trying out the POS without moving real money) and a <strong className="text-slate-800">live</strong> account (for real transactions). The active mode determines which account every checkout runs through.
          </p>
        )}
        <p className="text-[15px] font-medium text-slate-500 leading-relaxed">
          <strong className="text-slate-800 font-bold">Tip:</strong> use test card <code className="px-1.5 py-0.5 bg-slate-100 rounded text-sm">4242 4242 4242 4242</code> with any future expiration and any CVC when in test mode.
        </p>
      </div>
    </div>
  );
}

function SingleTenantPanel({ activeMode }: { activeMode: StripeMode }) {
  const isTest = activeMode === 'test';
  const dashboardUrl = isTest
    ? 'https://dashboard.stripe.com/test/dashboard'
    : 'https://dashboard.stripe.com/dashboard';

  return (
    <div className="bg-white p-6 rounded-[2rem] shadow-sm border border-slate-200/60 mb-8">
      <div className="flex items-center gap-3 mb-4">
        <div className={`w-12 h-12 rounded-2xl flex items-center justify-center border ${
          isTest
            ? 'bg-amber-50 text-amber-600 border-amber-200'
            : 'bg-blue-50 text-[#2563eb] border-blue-200'
        }`}>
          {isTest ? <FlaskConical className="w-6 h-6" /> : <Globe2 className="w-6 h-6" />}
        </div>
        <div>
          <h2 className="text-lg font-extrabold text-slate-900">
            {isTest ? 'Test Mode Active' : 'Live Mode Active'}
          </h2>
          <p className="text-[13px] font-medium text-slate-500">
            {isTest ? 'Sandbox transactions — no real money moves' : 'Production transactions — real money'}
          </p>
        </div>
      </div>
      <p className="text-[14px] font-medium text-slate-500 leading-relaxed mb-5">
        All transactions are processed through the platform's Stripe account.
        Use the mode toggle above to switch between test and live keys.
      </p>
      <a
        href={dashboardUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-2 px-5 py-3 bg-[#635BFF] text-white rounded-2xl font-bold text-[14px] hover:bg-[#4B45D6] transition-all active:scale-95"
      >
        <ExternalLink className="w-4 h-4" />
        Open Stripe Dashboard
      </a>
    </div>
  );
}

function ModeToggleButton({
  active,
  mode,
  onClick,
  disabled,
}: {
  active: boolean;
  mode: StripeMode;
  onClick: () => void;
  disabled?: boolean;
}) {
  const isTest = mode === 'test';
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`flex items-center gap-2 px-4 py-2 rounded-xl text-[14px] font-bold transition-all ${
        active
          ? isTest
            ? 'bg-amber-500 text-white shadow-md shadow-amber-500/30'
            : 'bg-[#2563eb] text-white shadow-md shadow-blue-900/20'
          : 'text-slate-500 hover:text-slate-700'
      } ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
    >
      {isTest ? <FlaskConical className="w-4 h-4" /> : <Globe2 className="w-4 h-4" />}
      {isTest ? 'Test' : 'Live'}
    </button>
  );
}

function StripeConnectionCard({
  mode,
  active,
  status,
  busy,
  onConnect,
  onDashboard,
  onDisconnect,
}: {
  mode: StripeMode;
  active: boolean;
  status: StripeModeStatus;
  busy: boolean;
  onConnect: () => void;
  onDashboard: () => void;
  onDisconnect: () => void;
}) {
  const isTest = mode === 'test';
  const accent = isTest
    ? { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', icon: 'text-amber-600', solid: 'bg-amber-500' }
    : { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200', icon: 'text-[#2563eb]', solid: 'bg-[#2563eb]' };

  return (
    <div className={`bg-white p-6 rounded-[2rem] shadow-sm border ${active ? accent.border : 'border-slate-200/60'} transition-all`}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className={`w-12 h-12 ${accent.bg} ${accent.icon} rounded-2xl flex items-center justify-center border ${accent.border}`}>
            {isTest ? <FlaskConical className="w-6 h-6" /> : <Globe2 className="w-6 h-6" />}
          </div>
          <div>
            <h2 className="text-lg font-extrabold text-slate-900 tracking-tight">{isTest ? 'Test Mode' : 'Live Mode'}</h2>
            <p className="text-[13px] font-medium text-slate-500">
              {isTest ? 'Sandbox transactions, no real money' : 'Production transactions, real money'}
            </p>
          </div>
        </div>
        {active && (
          <span className={`${accent.bg} ${accent.text} text-[11px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full border ${accent.border}`}>
            Active
          </span>
        )}
      </div>

      {!status.connected ? (
        <>
          <p className="text-[14px] font-medium text-slate-500 leading-relaxed mb-5">
            No {mode} account connected. Click below to link an existing Stripe account or create one.
          </p>
          <button
            onClick={onConnect}
            disabled={busy}
            className={`w-full flex items-center justify-center gap-2 px-5 py-3.5 ${accent.solid} text-white rounded-2xl font-bold text-[15px] hover:opacity-90 transition-all active:scale-95 shadow-md`}
          >
            {busy ? 'Redirecting…' : <>Connect {isTest ? 'Test' : 'Live'} Stripe <ChevronRight className="w-4 h-4" /></>}
          </button>
        </>
      ) : !status.onboardingComplete ? (
        <>
          <div className="flex items-center gap-2 mb-4">
            <ShieldAlert className="w-4 h-4 text-amber-500" />
            <span className="text-[13px] font-bold text-amber-700">Onboarding incomplete</span>
          </div>
          <StatusList status={status} />
          <div className="flex gap-2 mt-4">
            <button
              onClick={onConnect}
              disabled={busy}
              className={`flex-1 flex items-center justify-center gap-1.5 px-4 py-3 ${accent.solid} text-white rounded-2xl font-bold text-[14px] hover:opacity-90 transition-all active:scale-95`}
            >
              Continue Onboarding
            </button>
            <button
              onClick={onDisconnect}
              disabled={busy}
              className="px-4 py-3 bg-white text-red-600 border border-red-200 rounded-2xl font-bold text-[14px] hover:bg-red-50 transition-all active:scale-95"
            >
              Disconnect
            </button>
          </div>
        </>
      ) : (
        <>
          <div className="flex items-center gap-2 mb-3">
            <CheckCircle2 className={`w-4 h-4 ${accent.icon}`} />
            <span className={`text-[13px] font-bold ${accent.text}`}>Connected & ready</span>
          </div>
          <StatusList status={status} />
          <div className="flex gap-2 mt-4">
            <button
              onClick={onDashboard}
              disabled={busy}
              className="flex-1 flex items-center justify-center gap-1.5 px-4 py-3 bg-[#635BFF] text-white rounded-2xl font-bold text-[14px] hover:bg-[#4B45D6] transition-all active:scale-95"
            >
              <ExternalLink className="w-4 h-4" />
              Stripe Dashboard
            </button>
            <button
              onClick={onDisconnect}
              disabled={busy}
              className="px-4 py-3 bg-white text-slate-500 border border-slate-200 rounded-2xl font-bold text-[14px] hover:bg-red-50 hover:text-red-600 hover:border-red-200 transition-all active:scale-95"
            >
              Disconnect
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function StatusList({ status }: { status: StripeModeStatus }) {
  return (
    <div className="space-y-1.5 mt-2">
      <StatusRow label="Charges" enabled={status.chargesEnabled} />
      <StatusRow label="Payouts" enabled={status.payoutsEnabled} />
      <StatusRow label="Details" enabled={status.detailsSubmitted} />
    </div>
  );
}

function StatusRow({ label, enabled }: { label: string; enabled?: boolean }) {
  return (
    <div className="flex items-center justify-between px-3 py-2 bg-slate-50/70 rounded-xl border border-slate-100">
      <span className="text-[12px] font-bold text-slate-500 uppercase tracking-wide">{label}</span>
      <span className={`text-[13px] font-bold ${enabled ? 'text-[#2563eb]' : 'text-slate-400'}`}>
        {enabled ? 'Enabled' : 'Pending'}
      </span>
    </div>
  );
}
